import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { useUser } from '../../hooks/useUser';
import { createDocumentTag, listDocumentTags } from '../../services/documentTags';
import {
    assignDocument,
    downloadDocument,
    listDocuments,
    updateDocument,
    updateDocumentTags,
    uploadDocument
} from '../../services/documents';

const PAGE_LIMIT = 5;
const ASSIGNED_SCOPE = 'assigned';
const INTERNAL_SCOPE = 'internal';
const PERSONAL_SCOPE = 'personal';
const BULK_FETCH_LIMIT = 50;
const NO_TAGS_FILTER = '__NO_TAGS__';

const sectionCardSx = {
    p: { xs: 1.8, md: 2.2 },
    bgcolor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid #ECECEC',
    boxShadow: 'none'
};

const tabButtonSx = (active) => ({
    minHeight: 46,
    px: 2.2,
    borderRadius: '14px',
    border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
    bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
    color: active ? '#D63731' : '#3D3D3D',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
        boxShadow: 'none',
        bgcolor: active ? 'rgba(214, 55, 49, 0.08)' : '#F7F7F7'
    }
});

const actionIconButtonSx = {
    width: 40,
    height: 40,
    borderRadius: '12px',
    border: '1px solid #E2E2E2',
    color: '#3D3D3D',
    bgcolor: '#FFFFFF',
    '&:hover': {
        bgcolor: '#F7F7F7'
    }
};

const filterActionButtonSx = {
    width: 38,
    height: 38,
    borderRadius: '50%',
    border: '1px solid #E2E2E2',
    color: '#3D3D3D',
    bgcolor: '#FFFFFF',
    '&:hover': {
        bgcolor: '#F7F7F7'
    }
};

const uploadDropzoneSx = {
    minHeight: 180,
    borderRadius: '18px',
    border: '2.5px dashed #D63731',
    bgcolor: '#FCFCFC',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.2,
    cursor: 'pointer',
    px: 2,
    textAlign: 'center',
    transition: 'all 0.2s ease',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#D63731'
    }
};

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1,
    gap: 1
};

const secondaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px',
        boxShadow: 'none'
    }
};

const primaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#3D3D3D',
        boxShadow: 'none'
    }
};

const normalizeTagLabel = (tag) => tag?.tag || tag?.name || '';
const hasNoTagsFilter = (selected) => selected.includes(NO_TAGS_FILTER);
const realSelectedTags = (selected) => selected.filter((value) => value !== NO_TAGS_FILTER);
const matchesSelectedTags = (doc, selected) => {
    if (!selected.length) return false;

    const docTagUuids = (doc?.tags || []).map((tag) => tag.uuid).filter(Boolean);
    const realTags = realSelectedTags(selected);
    const tagMatch = realTags.some((uuid) => docTagUuids.includes(uuid));
    const noTagsMatch = hasNoTagsFilter(selected) && docTagUuids.length === 0;

    return tagMatch || noTagsMatch;
};

const CaregiverDocumentsSection = ({
    title = 'Documentos de Apoio',
    caregiverRoleUuid,
    initialScope = ASSIGNED_SCOPE,
    showScopeTabs = true
}) => {
    const { user } = useUser();
    const dropInputRef = useRef(null);
    const tagsInitializedRef = useRef(false);
    const queryKeyRef = useRef({
        [ASSIGNED_SCOPE]: '',
        [INTERNAL_SCOPE]: '',
        [PERSONAL_SCOPE]: ''
    });

    const [scope, setScope] = useState(initialScope);
    const [tags, setTags] = useState([]);
    const [selectedTagsByScope, setSelectedTagsByScope] = useState({
        [ASSIGNED_SCOPE]: [],
        [INTERNAL_SCOPE]: [],
        [PERSONAL_SCOPE]: []
    });

    const [docsByScope, setDocsByScope] = useState({
        [ASSIGNED_SCOPE]: [],
        [INTERNAL_SCOPE]: [],
        [PERSONAL_SCOPE]: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [pageIndexByScope, setPageIndexByScope] = useState({
        [ASSIGNED_SCOPE]: 0,
        [INTERNAL_SCOPE]: 0,
        [PERSONAL_SCOPE]: 0
    });

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFileObj, setUploadFileObj] = useState(null);
    const [uploadDocumentName, setUploadDocumentName] = useState('');
    const [uploadHidden, setUploadHidden] = useState(false);
    const [uploadTags, setUploadTags] = useState([]);
    const [uploadNewTag, setUploadNewTag] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const [createTagDialogOpen, setCreateTagDialogOpen] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [tagSubmitting, setTagSubmitting] = useState(false);
    const [tagError, setTagError] = useState('');

    const [documentTagsDialogOpen, setDocumentTagsDialogOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [documentTagsEdit, setDocumentTagsEdit] = useState([]);
    const [documentActionLoading, setDocumentActionLoading] = useState(false);
    const [documentActionError, setDocumentActionError] = useState('');

    const allTagUuids = useMemo(() => tags.map((tag) => tag.uuid), [tags]);
    const allTagFilterValues = useMemo(() => [...allTagUuids, NO_TAGS_FILTER], [allTagUuids]);
    const selectedTagUuids = selectedTagsByScope[scope] || [];
    const pageIndex = pageIndexByScope[scope] || 0;
    const scopeDocs = docsByScope[scope] || [];
    const docs = useMemo(() => {
        const start = pageIndex * PAGE_LIMIT;
        return scopeDocs.slice(start, start + PAGE_LIMIT);
    }, [pageIndex, scopeDocs]);
    const hasNextPage = (pageIndex + 1) * PAGE_LIMIT < scopeDocs.length;
    const totalPages = Math.max(1, Math.ceil(scopeDocs.length / PAGE_LIMIT));

    useEffect(() => {
        setScope(initialScope);
    }, [initialScope]);

    useEffect(() => {
        const loadTags = async () => {
            if (!user.selectedRole) return;
            try {
                const response = await listDocumentTags({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });
                const rows = Array.isArray(response?.data) ? response.data : [];
                const loadedUuids = rows.map((tag) => tag.uuid);
                setTags(rows);

                setSelectedTagsByScope((prev) => {
                    if (!tagsInitializedRef.current) {
                        tagsInitializedRef.current = true;
                        return {
                            [ASSIGNED_SCOPE]: [...loadedUuids, NO_TAGS_FILTER],
                            [INTERNAL_SCOPE]: [...loadedUuids, NO_TAGS_FILTER],
                            [PERSONAL_SCOPE]: [...loadedUuids, NO_TAGS_FILTER]
                        };
                    }

                    return {
                        [ASSIGNED_SCOPE]: (prev[ASSIGNED_SCOPE] || []).filter(
                            (uuid) => uuid === NO_TAGS_FILTER || loadedUuids.includes(uuid)
                        ),
                        [INTERNAL_SCOPE]: (prev[INTERNAL_SCOPE] || []).filter(
                            (uuid) => uuid === NO_TAGS_FILTER || loadedUuids.includes(uuid)
                        ),
                        [PERSONAL_SCOPE]: (prev[PERSONAL_SCOPE] || []).filter(
                            (uuid) => uuid === NO_TAGS_FILTER || loadedUuids.includes(uuid)
                        )
                    };
                });
            } catch {
                setTags([]);
            }
        };

        void loadTags();
    }, [user.selectedRole]);

    useEffect(() => {
        setPageIndexByScope((prev) => ({
            ...prev,
            [scope]: 0
        }));
    }, [scope, selectedTagsByScope[scope]]);

    const setSelectedTagsForScope = useCallback((targetScope, nextValue) => {
        setSelectedTagsByScope((prev) => ({
            ...prev,
            [targetScope]: nextValue
        }));
    }, []);

    const loadDocuments = useCallback(async () => {
        if (!user.selectedRole || !caregiverRoleUuid) return;

        if (selectedTagUuids.length === 0) {
            setDocsByScope((prev) => ({
                ...prev,
                [scope]: []
            }));
            return;
        }

        setLoading(true);
        setError('');

        try {
            let paginationToken = null;
            let nextToken = null;
            const collected = [];
            const seenTokens = new Set();

            do {
                if (paginationToken && seenTokens.has(paginationToken)) {
                    break;
                }
                if (paginationToken) {
                    seenTokens.add(paginationToken);
                }

                const response = await listDocuments({
                    userRoleUuid: user.selectedRole,
                    caregiverRoleUuid,
                    paginationToken,
                    paginationLimit: BULK_FETCH_LIMIT,
                    assignedOnly: scope === ASSIGNED_SCOPE || scope === INTERNAL_SCOPE,
                    hiddenOnly: scope === INTERNAL_SCOPE,
                    uploadedOnly: scope === PERSONAL_SCOPE
                });

                const rows = Array.isArray(response?.data) ? response.data : [];
                collected.push(
                    ...rows.filter((doc) => {
                        if (scope === ASSIGNED_SCOPE && doc?.hidden) {
                            return false;
                        }

                        return matchesSelectedTags(doc, selectedTagUuids);
                    })
                );

                nextToken = response?.headers?.['pagination-token'] || null;
                paginationToken = nextToken;
            } while (nextToken);

            setDocsByScope((prev) => ({
                ...prev,
                [scope]: collected
            }));
        } catch (err) {
            setDocsByScope((prev) => ({
                ...prev,
                [scope]: []
            }));
            setError(err?.response?.data?.description || 'Nao foi possivel carregar documentos.');
        } finally {
            setLoading(false);
        }
    }, [
        caregiverRoleUuid,
        scope,
        selectedTagUuids,
        user.selectedRole
    ]);

    useEffect(() => {
        const key = JSON.stringify({
            role: user.selectedRole || '',
            caregiver: caregiverRoleUuid || '',
            scope,
            tags: selectedTagUuids
        });

        if (queryKeyRef.current[scope] === key) {
            return;
        }

        queryKeyRef.current[scope] = key;
        void loadDocuments();
    }, [caregiverRoleUuid, loadDocuments, scope, selectedTagUuids, user.selectedRole]);

    const createTag = useCallback(
        async (tagName, onCreated) => {
            if (!user.selectedRole || !tagName.trim()) return;

            setTagSubmitting(true);
            setTagError('');

            try {
                const response = await createDocumentTag({
                    userRoleUuid: user.selectedRole,
                    tag: tagName.trim()
                });

                const created = response?.data;
                if (!created?.uuid) return;

                setTags((prev) => [...prev, created]);
                setSelectedTagsByScope((prev) => ({
                    [ASSIGNED_SCOPE]: prev[ASSIGNED_SCOPE]?.includes(created.uuid)
                        ? prev[ASSIGNED_SCOPE]
                        : [...(prev[ASSIGNED_SCOPE] || []), created.uuid],
                    [PERSONAL_SCOPE]: prev[PERSONAL_SCOPE]?.includes(created.uuid)
                        ? prev[PERSONAL_SCOPE]
                        : [...(prev[PERSONAL_SCOPE] || []), created.uuid]
                }));

                if (typeof onCreated === 'function') {
                    onCreated(created);
                }
            } catch (err) {
                setTagError(err?.response?.data?.description || 'Nao foi possivel criar a tag.');
            } finally {
                setTagSubmitting(false);
            }
        },
        [user.selectedRole]
    );

    const handlePickedFile = (file) => {
        if (!file) return;
        setUploadError('');
        setUploadFileObj(file);
        setUploadDocumentName('');
        setUploadHidden(false);
        setUploadTags([]);
        setUploadNewTag('');
        setUploadDialogOpen(true);
    };

    const handleUpload = async () => {
        if (!user.selectedRole || !uploadFileObj || !caregiverRoleUuid) return;

        setUploading(true);
        setUploadError('');

        try {
            const uploadResp = await uploadDocument({
                userRoleUuid: user.selectedRole,
                file: uploadFileObj,
                tags: uploadTags,
                hidden: uploadHidden
            });

            const createdDocUuid = uploadResp?.data?.uuid || null;
            if (createdDocUuid) {
                const requestedName = uploadDocumentName.trim();

                if (requestedName) {
                    await updateDocument({
                        userRoleUuid: user.selectedRole,
                        documentUuid: createdDocUuid,
                        name: requestedName
                    });
                }

                await assignDocument({
                    userRoleUuid: user.selectedRole,
                    documentUuid: createdDocUuid,
                    caregiverRoleUuid
                });
            }

            setUploadDialogOpen(false);
            setUploadFileObj(null);
            setUploadDocumentName('');
            setUploadHidden(false);
            setUploadTags([]);
            setUploadNewTag('');
            setPageIndexByScope((prev) => ({
                ...prev,
                [ASSIGNED_SCOPE]: 0,
                [INTERNAL_SCOPE]: 0
            }));
            queryKeyRef.current[ASSIGNED_SCOPE] = '';
            queryKeyRef.current[INTERNAL_SCOPE] = '';

            if (scope === ASSIGNED_SCOPE || scope === INTERNAL_SCOPE) {
                await loadDocuments();
            }
        } catch (err) {
            setUploadError(err?.response?.data?.description || 'Falha no upload do documento.');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (doc) => {
        if (!user.selectedRole || !doc?.uuid) return;

        try {
            const resp = await downloadDocument({
                userRoleUuid: user.selectedRole,
                documentUuid: doc.uuid
            });
            const blob = new Blob([resp.data], {
                type: resp.headers?.['content-type'] || 'application/octet-stream'
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = doc.name || `documento.${doc.extension || 'bin'}`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err?.response?.data?.description || 'Nao foi possivel descarregar o documento.');
        }
    };

    const handleSaveDocumentTags = async () => {
        if (!user.selectedRole || !selectedDocument?.uuid) return;

        setDocumentActionLoading(true);
        setDocumentActionError('');

        try {
            await updateDocumentTags({
                userRoleUuid: user.selectedRole,
                documentUuid: selectedDocument.uuid,
                tags: documentTagsEdit
            });
            setDocumentTagsDialogOpen(false);
            queryKeyRef.current[scope] = '';
            await loadDocuments();
        } catch (err) {
            setDocumentActionError(err?.response?.data?.description || 'Nao foi possivel atualizar tags.');
        } finally {
            setDocumentActionLoading(false);
        }
    };

    const handleCreateTagFromDialog = async () => {
        await createTag(newTagName, () => {
            setCreateTagDialogOpen(false);
            setNewTagName('');
        });
    };

    const handleCreateTagInUpload = async () => {
        await createTag(uploadNewTag, (created) => {
            setUploadTags((prev) => (prev.includes(created.uuid) ? prev : [...prev, created.uuid]));
            setUploadNewTag('');
        });
    };

    const handleSelectAllTags = () => {
        setSelectedTagsForScope(scope, allTagFilterValues);
    };

    const handleClearTags = () => {
        setSelectedTagsForScope(scope, []);
    };

    const handleToggleTag = (tagUuid) => {
        setSelectedTagsByScope((prev) => {
            const current = prev[scope] || [];
            const exists = current.includes(tagUuid);
            return {
                ...prev,
                [scope]: exists ? current.filter((uuid) => uuid !== tagUuid) : [...current, tagUuid]
            };
        });
    };

    const openDocumentTagsDialog = (doc) => {
        setSelectedDocument(doc || null);
        setDocumentTagsEdit(Array.isArray(doc?.tags) ? doc.tags.map((tag) => tag.uuid) : []);
        setDocumentActionError('');
        setDocumentTagsDialogOpen(true);
    };

    const renderTagFilters = () => (
        <Stack spacing={1.2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={700} color="#3D3D3D">
                    Filtrar por tags
                </Typography>
                <Stack direction="row" spacing={0.8}>
                    <Tooltip title="Selecionar todas as tags">
                        <IconButton onClick={handleSelectAllTags} sx={filterActionButtonSx}>
                            <DoneAllIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Limpar seleção de tags">
                        <IconButton onClick={handleClearTags} sx={filterActionButtonSx}>
                            <RemoveDoneIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Criar nova tag">
                        <IconButton
                            onClick={() => {
                                setTagError('');
                                setNewTagName('');
                                setCreateTagDialogOpen(true);
                            }}
                            sx={filterActionButtonSx}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                    label="Sem tags"
                    clickable
                    onClick={() => handleToggleTag(NO_TAGS_FILTER)}
                    sx={{
                        borderRadius: '12px',
                        height: 38,
                        px: 0.6,
                        border: selectedTagUuids.includes(NO_TAGS_FILTER)
                            ? '2px solid #D63731'
                            : '2px solid #E2E2E2',
                        bgcolor: selectedTagUuids.includes(NO_TAGS_FILTER)
                            ? 'rgba(214, 55, 49, 0.05)'
                            : '#FFFFFF',
                        color: selectedTagUuids.includes(NO_TAGS_FILTER) ? '#D63731' : '#3D3D3D',
                        fontWeight: 700,
                        '& .MuiChip-label': {
                            px: 1.1
                        }
                    }}
                />
                {tags.map((tag) => {
                    const active = selectedTagUuids.includes(tag.uuid);
                    return (
                        <Chip
                            key={tag.uuid}
                            label={normalizeTagLabel(tag)}
                            clickable
                            onClick={() => handleToggleTag(tag.uuid)}
                            sx={{
                                borderRadius: '12px',
                                height: 38,
                                px: 0.6,
                                border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
                                bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                color: active ? '#D63731' : '#3D3D3D',
                                fontWeight: 700,
                                '& .MuiChip-label': {
                                    px: 1.1
                                }
                            }}
                        />
                    );
                })}
            </Stack>
        </Stack>
    );

    const renderDocumentRow = (doc) => (
        <Paper
            key={doc.uuid}
            sx={{
                px: 1.8,
                py: 1.35,
                display: 'grid',
                gap: 1.2,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr) auto' },
                alignItems: 'center',
                bgcolor: '#ECECEC',
                borderRadius: '14px',
                boxShadow: 'none'
            }}
        >
            <Stack spacing={0.8} minWidth={0}>
                <Typography variant="body1" fontWeight={700} color="#3D3D3D" noWrap>
                    {doc.name}
                </Typography>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    {(doc.tags || []).length > 0 ? (
                        doc.tags.map((tag) => (
                            <Chip
                                key={tag.uuid}
                                label={normalizeTagLabel(tag)}
                                size="small"
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #E2E2E2',
                                    color: '#3D3D3D',
                                    fontWeight: 600
                                }}
                            />
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Sem tags
                        </Typography>
                    )}
                </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary" noWrap>
                {scope === ASSIGNED_SCOPE || scope === INTERNAL_SCOPE
                    ? `Upload de ${doc?.uploader?.name || '--'}`
                    : `.${doc.extension || '--'}`}
            </Typography>

            <Stack direction="row" spacing={0.8} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Tooltip title="Editar tags">
                    <IconButton onClick={() => openDocumentTagsDialog(doc)} sx={actionIconButtonSx}>
                        <LocalOfferIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Transferir documento">
                    <IconButton onClick={() => void handleDownload(doc)} sx={actionIconButtonSx}>
                        <DownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Paper>
    );

    return (
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
            <Typography variant="h5" fontWeight={700} color="#3D3D3D">
                {title}
            </Typography>

            {showScopeTabs ? (
                <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Ver documentos internos">
                        <Button onClick={() => setScope(INTERNAL_SCOPE)} sx={tabButtonSx(scope === INTERNAL_SCOPE)}>
                            Documentos Internos
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver documentos atribuídos ao cuidador">
                        <Button onClick={() => setScope(ASSIGNED_SCOPE)} sx={tabButtonSx(scope === ASSIGNED_SCOPE)}>
                            Documentos Atribuídos
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver documentos pessoais do cuidador">
                        <Button onClick={() => setScope(PERSONAL_SCOPE)} sx={tabButtonSx(scope === PERSONAL_SCOPE)}>
                            Documentos Pessoais
                        </Button>
                    </Tooltip>
                </Stack>
            ) : null}

            <Paper
                sx={{
                    ...sectionCardSx,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden'
                }}
            >
                {scope === ASSIGNED_SCOPE || scope === INTERNAL_SCOPE ? (
                    <Box
                        sx={{
                            height: '100%',
                            minHeight: 0,
                            display: 'grid',
                            gridTemplateRows: 'auto 1fr',
                            gap: 2
                        }}
                    >
                        {renderTagFilters()}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.7fr) minmax(300px, 0.95fr)' },
                                gap: 2,
                                height: '100%',
                                minHeight: 0
                            }}
                        >
                            <Stack spacing={1.4} sx={{ minHeight: 0 }}>
                                {loading ? (
                                    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size={26} />
                                    </Box>
                                ) : error ? (
                                    <Typography color="error">{error}</Typography>
                                ) : selectedTagUuids.length === 0 ? (
                                    <Typography color="text.secondary">Nenhuma tag selecionada.</Typography>
                                ) : docs.length === 0 ? (
                                    <Typography color="text.secondary">Sem documentos para mostrar.</Typography>
                                ) : (
                                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
                                        <Stack spacing={1}>
                                            {docs.map((doc) => renderDocumentRow(doc))}
                                        </Stack>
                                    </Box>
                                )}

                                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 0.5, alignItems: 'center' }}>
                                    <Tooltip title="Página anterior">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setPageIndexByScope((prev) => ({
                                                        ...prev,
                                                        [scope]: Math.max((prev[scope] || 0) - 1, 0)
                                                    }))
                                                }
                                                disabled={pageIndex <= 0}
                                            >
                                                <KeyboardArrowLeftIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28, textAlign: 'center' }}>
                                        {`${pageIndex + 1} de ${totalPages}`}
                                    </Typography>
                                    <Tooltip title="Página seguinte">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (!hasNextPage) return;
                                                    setPageIndexByScope((prev) => ({
                                                        ...prev,
                                                        [scope]: (prev[scope] || 0) + 1
                                                    }));
                                                }}
                                                disabled={!hasNextPage}
                                            >
                                                <KeyboardArrowRightIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Stack>

                            <Box
                                onClick={() => dropInputRef.current?.click()}
                                onDrop={(event) => {
                                    event.preventDefault();
                                    handlePickedFile(event.dataTransfer?.files?.[0] || null);
                                }}
                                onDragOver={(event) => event.preventDefault()}
                                sx={{
                                    ...uploadDropzoneSx,
                                    minHeight: { xs: 180, lg: '100%' }
                                }}
                            >
                                <CloudUploadOutlinedIcon sx={{ fontSize: 30, color: '#D63731' }} />
                                <Typography variant="body1" fontWeight={700} color="#3D3D3D">
                                    Adicionar documento
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Arrasta um ficheiro para aqui ou clica para selecionar.
                                </Typography>
                                <input
                                    ref={dropInputRef}
                                    hidden
                                    type="file"
                                    onChange={(event) => handlePickedFile(event.target.files?.[0] || null)}
                                />
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
                        {renderTagFilters()}

                        {loading ? (
                            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={26} />
                            </Box>
                        ) : error ? (
                            <Typography color="error">{error}</Typography>
                        ) : selectedTagUuids.length === 0 ? (
                            <Typography color="text.secondary">Nenhuma tag selecionada.</Typography>
                        ) : docs.length === 0 ? (
                            <Typography color="text.secondary">Sem documentos para mostrar.</Typography>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
                                <Stack spacing={1}>
                                    {docs.map((doc) => renderDocumentRow(doc))}
                                </Stack>
                            </Box>
                        )}

                        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Página anterior">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setPageIndexByScope((prev) => ({
                                                ...prev,
                                                [PERSONAL_SCOPE]: Math.max((prev[PERSONAL_SCOPE] || 0) - 1, 0)
                                            }))
                                        }
                                        disabled={pageIndex <= 0}
                                    >
                                        <KeyboardArrowLeftIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28, textAlign: 'center' }}>
                                {`${pageIndex + 1} de ${totalPages}`}
                            </Typography>
                            <Tooltip title="Página seguinte">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (!hasNextPage) return;
                                            setPageIndexByScope((prev) => ({
                                                ...prev,
                                                [PERSONAL_SCOPE]: (prev[PERSONAL_SCOPE] || 0) + 1
                                            }));
                                        }}
                                        disabled={!hasNextPage}
                                    >
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Stack>
                )}
            </Paper>

            <Dialog
                open={uploadDialogOpen}
                onClose={() => !uploading && setUploadDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Upload de Documento</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="#3D3D3D">
                            Ficheiro: <strong>{uploadFileObj?.name || '--'}</strong>
                        </Typography>

                        <TextField
                            size="small"
                            fullWidth
                            label="Nome do documento"
                            placeholder={uploadFileObj?.name || 'Nome do documento'}
                            value={uploadDocumentName}
                            onChange={(event) => setUploadDocumentName(event.target.value)}
                            helperText="Se deixar em branco, fica com o nome original do ficheiro."
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={uploadHidden}
                                    onChange={(event) => setUploadHidden(event.target.checked)}
                                    color="error"
                                />
                            }
                            label="Documento interno (escondido do cuidador)"
                        />

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {tags.map((tag) => {
                                const active = uploadTags.includes(tag.uuid);
                                return (
                                    <Chip
                                        key={tag.uuid}
                                        label={normalizeTagLabel(tag)}
                                        clickable
                                        onClick={() =>
                                            setUploadTags((prev) =>
                                                prev.includes(tag.uuid)
                                                    ? prev.filter((uuid) => uuid !== tag.uuid)
                                                    : [...prev, tag.uuid]
                                            )
                                        }
                                        sx={{
                                            borderRadius: '12px',
                                            height: 38,
                                            px: 0.6,
                                            border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
                                            bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                            color: active ? '#D63731' : '#3D3D3D',
                                            fontWeight: 700,
                                            '& .MuiChip-label': {
                                                px: 1.1
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Nova tag"
                                value={uploadNewTag}
                                onChange={(event) => setUploadNewTag(event.target.value)}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => void handleCreateTagInUpload()}
                                disabled={tagSubmitting || !uploadNewTag.trim()}
                                sx={secondaryDialogButtonSx}
                            >
                                Criar tag
                            </Button>
                        </Stack>

                        {uploadError ? (
                            <Typography variant="caption" color="error">
                                {uploadError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => {
                            setUploadDialogOpen(false);
                            setUploadFileObj(null);
                            setUploadDocumentName('');
                            setUploadHidden(false);
                            setUploadTags([]);
                            setUploadNewTag('');
                            setUploadError('');
                        }}
                        disabled={uploading}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void handleUpload()}
                        disabled={uploading}
                        sx={primaryDialogButtonSx}
                    >
                        {uploading ? 'A carregar...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={createTagDialogOpen}
                onClose={() => setCreateTagDialogOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Criar Nova Tag</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Nome da tag"
                            value={newTagName}
                            onChange={(event) => setNewTagName(event.target.value)}
                        />
                        {tagError ? (
                            <Typography variant="caption" color="error">
                                {tagError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button onClick={() => setCreateTagDialogOpen(false)} variant="outlined" sx={secondaryDialogButtonSx}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => void handleCreateTagFromDialog()}
                        disabled={tagSubmitting || !newTagName.trim()}
                        variant="contained"
                        sx={primaryDialogButtonSx}
                    >
                        Criar tag
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={documentTagsDialogOpen}
                onClose={() => setDocumentTagsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Editar Tags do Documento</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="#3D3D3D">
                            Nome: <strong>{selectedDocument?.name || '--'}</strong>
                        </Typography>

                        <Stack spacing={1.2}>
                            <Typography variant="subtitle2" fontWeight={700} color="#3D3D3D">
                                Tags
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {tags.map((tag) => {
                                    const active = documentTagsEdit.includes(tag.uuid);
                                    return (
                                        <Chip
                                            key={tag.uuid}
                                            label={normalizeTagLabel(tag)}
                                            clickable
                                            onClick={() =>
                                                setDocumentTagsEdit((prev) =>
                                                    prev.includes(tag.uuid)
                                                        ? prev.filter((uuid) => uuid !== tag.uuid)
                                                        : [...prev, tag.uuid]
                                                )
                                            }
                                            sx={{
                                                borderRadius: '12px',
                                                height: 38,
                                                px: 0.6,
                                                border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
                                                bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                                color: active ? '#D63731' : '#3D3D3D',
                                                fontWeight: 700,
                                                '& .MuiChip-label': {
                                                    px: 1.1
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        </Stack>

                        {documentActionError ? (
                            <Typography variant="caption" color="error">
                                {documentActionError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => setDocumentTagsDialogOpen(false)}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Fechar
                    </Button>
                    <Button
                        onClick={() => void handleSaveDocumentTags()}
                        disabled={documentActionLoading}
                        variant="contained"
                        sx={primaryDialogButtonSx}
                    >
                        Guardar tags
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default CaregiverDocumentsSection;


